import * as RadioGroup from '@radix-ui/react-radio-group';
import * as React from 'react';

import classnames from '../utils/classnames.tsx';

type SegmentedButtonVariant = 'track' | 'joined';

const VariantContext = React.createContext<SegmentedButtonVariant>('track');

const SegmentedButtonRoot = ({
    className,
    variant = 'track',
    ...props
}: React.ComponentPropsWithRef<typeof RadioGroup.Root> & { variant?: SegmentedButtonVariant }) => (
    <VariantContext.Provider value={variant}>
        <RadioGroup.Root
            orientation="horizontal"
            {...props}
            className={classnames(
                'inline-flex items-center self-start',
                variant === 'track' ? 'gap-0.5 p-0.5 rounded bg-surface-medium' : 'flex-wrap rounded shadow-control',
                className
            )}
        />
    </VariantContext.Provider>
);

const SegmentedButtonItem = ({ className, ...props }: React.ComponentPropsWithRef<typeof RadioGroup.Item>) => {
    const variant = React.useContext(VariantContext);
    return (
        <RadioGroup.Item
            {...props}
            className={classnames(
                'h-8 px-3 flex items-center gap-2 border border-solid text-sm font-medium cursor-pointer transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                variant === 'track' && [
                    'rounded border-line-transparent text-content-medium',
                    'hover:bg-surface-strong hover:text-content-strong',
                    'data-[state=checked]:bg-surface-weak data-[state=checked]:border-line-strong data-[state=checked]:shadow-control data-[state=checked]:text-content-strong',
                ],
                variant === 'joined' && [
                    'relative -ml-px first:ml-0 rounded-none first:rounded-l last:rounded-r border-line-strong text-content-strong',
                    'hover:bg-surface-medium data-[state=checked]:hover:bg-surface-brand-weak',
                    'data-[state=checked]:z-10 data-[state=checked]:bg-surface-brand-weak data-[state=checked]:border-line-brand-default data-[state=checked]:text-content-brand-strong',
                ],
                className
            )}
        />
    );
};

export default Object.assign(SegmentedButtonRoot, {
    Item: SegmentedButtonItem,
});
